import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ModalsManager } from '@/components/modals/modalsManager';
import { Badge, Button } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { subscriptionApis } from '@/features/subscription';
import { AutoReloadModal } from '@/features/subscription/auto-reload-modal';
import { BuyUsageCreditsModal } from '@/features/subscription/buy-usage-credits-modal';
import {
  creditsToValueCents,
  formatCentsExact,
} from '@/features/subscription/usage-format';

import { teamApis } from '../team.query';

import type { TeamAutoReloadSettings } from '../team.types';

export const TeamUsageCreditsCard = ({ teamId }: { teamId: string }) => {
  const { data: wallet, refetch } = useQuery({
    queryKey: ['team-wallet', teamId],
    queryFn: () => teamApis.getWallet(teamId),
  });
  const { data: packs } = useQuery({
    queryKey: ['credit-packs'],
    queryFn: subscriptionApis.getCreditPacks,
  });

  if (!wallet) return null;

  const rateCentsPer1000 = wallet.rate_cents_per_1000;
  const paymentMethod = wallet.payment_method ?? null;
  const maxDiscountPct = Math.max(
    0,
    ...(packs?.discount_brackets ?? []).map((bracket) => bracket.discount_pct)
  );

  const openBuyModal = () =>
    ModalsManager.showResolvable(BuyUsageCreditsModal, {
      teamId,
      paymentMethodOverride: paymentMethod,
      onEditPaymentMethod: () => teamApis.createPortal(teamId),
    });

  return (
    <YStack className="gap-4 px-6 py-4 bg-surface border border-border dark:border-border-strong rounded-xl">
      <YStack className="gap-0.5">
        <span className="text-base font-medium text-foreground">
          Usage credits
        </span>
        <span className="text-sm text-foreground-muted">
          Buy usage credits so your team can keep using Littlebird if they hit a
          limit.
        </span>
      </YStack>

      <XStack className="items-center justify-between gap-4">
        <YStack className="gap-0.5">
          <span className="text-xl font-semibold text-foreground">
            {rateCentsPer1000 != null
              ? formatCentsExact(
                  creditsToValueCents(
                    Math.max(0, wallet.team_wallet.purchased_balance),
                    rateCentsPer1000
                  )
                )
              : '—'}
          </span>
          <span className="text-sm text-foreground-muted">Current balance</span>
        </YStack>
        <Button
          variant="outline"
          className="shrink-0 gap-2"
          disabled={!paymentMethod}
          onClick={openBuyModal}
        >
          Buy usage credits
          {maxDiscountPct > 0 && (
            <Badge variant="default" size="xs">
              Up to {maxDiscountPct}% off
            </Badge>
          )}
        </Button>
      </XStack>

      <TeamAutoReloadRow
        teamId={teamId}
        autoReload={wallet.auto_reload ?? { enabled: false }}
        onChanged={() => refetch()}
      />
    </YStack>
  );
};

const TeamAutoReloadRow = ({
  teamId,
  autoReload,
  onChanged,
}: {
  teamId: string;
  autoReload: TeamAutoReloadSettings;
  onChanged: () => void;
}) => {
  const [busy, setBusy] = useState(false);

  const thresholdValueCents = autoReload.threshold_value_cents ?? 0;
  const topupValueCents = autoReload.topup_value_cents ?? 0;

  const openModal = async () => {
    const saved = await ModalsManager.showResolvable(AutoReloadModal, {
      initialThresholdValueCents: autoReload.threshold_value_cents ?? undefined,
      initialTopupValueCents: autoReload.topup_value_cents ?? undefined,
      initialMonthlyCapDollars: autoReload.monthly_cap_cents
        ? autoReload.monthly_cap_cents / 100
        : undefined,
      submitAutoReload: (request) => teamApis.updateAutoReload(teamId, request),
    });
    if (saved) onChanged();
  };

  const turnOff = async () => {
    setBusy(true);
    await teamApis.updateAutoReload(teamId, { enabled: false });
    setBusy(false);
    onChanged();
  };

  return (
    <XStack className="items-center justify-between gap-3 pt-4 border-t border-border">
      <YStack className="gap-0.5">
        <span className="text-sm font-medium text-foreground">Auto-reload</span>
        <span className="text-sm text-foreground-muted">
          {autoReload.enabled
            ? `When the team pool drops below ${formatCentsExact(thresholdValueCents)}, top up with ${formatCentsExact(topupValueCents)}.`
            : 'Automatically buy more usage credits when your team is running low'}
        </span>
        {autoReload.last_failure_reason && (
          <span className="text-xs text-destructive">
            {autoReload.last_failure_reason === 'Monthly recharge cap reached'
              ? 'Monthly limit reached. Auto-reload resumes next month.'
              : "The last reload didn't go through. Update the team's payment method and try again."}
          </span>
        )}
      </YStack>
      <XStack className="gap-2 shrink-0">
        {autoReload.enabled && (
          <Button
            variant="outline"
            size="sm"
            onClick={turnOff}
            isLoading={busy}
            disabled={busy}
          >
            Turn off
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={openModal} disabled={busy}>
          {autoReload.enabled ? 'Edit' : 'Turn on'}
        </Button>
      </XStack>
    </XStack>
  );
};
